import React from "react";
import styled from "@emotion/styled";

import { useGetResolutionsQuery } from "../../../api/answers";
import CloseIcon from "../../../components/icons/CloseIcon";
import Loading from "../../../components/Loading";
import ErrorMessage from "../../../components/Error";
import { ParagraphHeader } from "../../../components/typography/Paragraph";
import {
  ModalContainer,
  ModalHeader,
  ModalContent,
  StyledCard,
  StyledCloseButton,
  StyledOverlay,
} from "./DisputeComponents";
import { ResolutionCard } from "./ResolutionCard";
import { FLEX_COLUMN, SPACING } from "../../../components/theme";

const ResolutionsContainer = styled.div`
  ${FLEX_COLUMN}
  gap: ${SPACING.LG};
  margin-top: ${SPACING.MD};
`;

const EmptyStateContainer = styled.div`
  ${FLEX_COLUMN}
  text-align: center;
  padding: ${SPACING.XL};
`;

interface ResolutionsModalProps {
  onClose: () => void;
  onResolutionChanged: () => void;
  userId: string;
  documentId: string;
}

export const ResolutionsModal: React.FC<ResolutionsModalProps> = ({
  onClose,
  onResolutionChanged,
  userId,
  documentId,
}) => {
  const {
    data: resolutions,
    isLoading,
    error,
  } = useGetResolutionsQuery(
    {
      userId,
      documentId,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <StyledOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <StyledCard>
          <ModalHeader>
            <ParagraphHeader fontWeight={700}>
              Alignment History
            </ParagraphHeader>
            <StyledCloseButton onClick={onClose}>
              <CloseIcon />
            </StyledCloseButton>
          </ModalHeader>

          <ModalContent>
            {isLoading && <Loading />}

            {error && (
              <ErrorMessage>
                Could not load alignment history. Please try again.
              </ErrorMessage>
            )}

            {!isLoading && !error && Array.isArray(resolutions) && (
              <>
                {resolutions.length === 0 ? (
                  <EmptyStateContainer>
                    <ParagraphHeader>
                      No resolved alignments yet. When you and your partner
                      agree on answers, they will appear here.
                    </ParagraphHeader>
                  </EmptyStateContainer>
                ) : (
                  <ResolutionsContainer>
                    {resolutions.map((resolution) => (
                      <ResolutionCard
                        key={resolution.id}
                        resolution={resolution}
                        currentUserId={userId}
                        onResolutionChanged={onResolutionChanged}
                      />
                    ))}
                  </ResolutionsContainer>
                )}
              </>
            )}
          </ModalContent>
        </StyledCard>
      </ModalContainer>
    </StyledOverlay>
  );
};
